import { Settings } from "@mui/icons-material";
import { Box, IconButton, styled } from "@mui/material";
import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { CORE_PANEL_IDS } from "contexts/sidePanels";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { selectIsLargeMapMobileOpen } from "features/miniMap/minimapSlice";
import { useIsPanelOpenUnified, useUnifiedPanelToggle } from "hooks/useUnifiedPanelToggle";

import { PROCORE_TOP_BAR, TOOLBAR_BUTTON, TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "../constants";

const ButtonsContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ButtonBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${PROCORE_TOP_BAR.ACTION_SIZE}px;
    height: ${PROCORE_TOP_BAR.ACTION_SIZE}px;
    background-color: ${TOOLBAR_CONTAINER.BG};
    border-radius: ${TOOLBAR_CONTAINER.RADIUS}px;
    border: ${TOOLBAR_CONTAINER.BORDER};
    box-shadow: ${TOOLBAR_CONTAINER.SHADOW};
`;

const ActionButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
    width: "100%",
    height: "100%",
    padding: 0,
    borderRadius: `${TOOLBAR_BUTTON.RADIUS}px`,
    backgroundColor: active ? TOOLBAR_COLORS.ACTIVE_BG : "transparent",
    color: active ? TOOLBAR_COLORS.ACTIVE : TOOLBAR_COLORS.DEFAULT,
    transition: "background-color 0.15s ease",
    "&:hover": {
        backgroundColor: active ? TOOLBAR_COLORS.ACTIVE_HOVER : TOOLBAR_COLORS.HOVER,
    },
    "& svg": {
        width: "20px",
        height: "20px",
    },
}));

interface ActionButtonRendererProps {
    toolbarId: string;
}

/**
 * Custom button renderer for top bar action buttons
 */
function ActionButtonRenderer({ toolbarId }: ActionButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);
    const isSettingsOpen = useIsPanelOpenUnified(CORE_PANEL_IDS.SETTINGS);
    const isLargeMapOpen = useAppSelector(selectIsLargeMapMobileOpen);

    if (buttons.length === 0 || isLargeMapOpen) {
        return null;
    }

    return (
        <ButtonsContainer>
            {buttons.map((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (!state.visible) {
                    return null;
                }

                // Settings button active state comes from panel state, not toolbar state
                const isActive = config.id === "settings" ? isSettingsOpen : state.active;

                return (
                    <ButtonBox key={config.id}>
                        <ActionButton onClick={config.onClick} title={config.title} active={isActive}>
                            {config.icon}
                        </ActionButton>
                    </ButtonBox>
                );
            })}
        </ButtonsContainer>
    );
}

/**
 * Action buttons - right side of top bar
 *
 * Renders all registered action buttons (e.g. Settings) in both landscape and portrait mode.
 */
export function ActionButtons() {
    const toolbarDispatch = useDispatchToolbars();
    const { togglePanel } = useUnifiedPanelToggle();

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: "settings",
                    icon: <Settings />,
                    onClick: () => togglePanel(CORE_PANEL_IDS.SETTINGS),
                    title: "Settings",
                },
                state: {
                    active: false, // Active state comes from panel state
                    disabled: false,
                    visible: true,
                },
            },
            // {
            //     config: {
            //         id: "info",
            //         icon: <InfoOutlined />,
            //         onClick: () => console.log("Info clicked"),
            //         title: "Info",
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: "menu",
            //         icon: <MoreVert />,
            //         onClick: () => console.log("Menu clicked"),
            //         title: "More options",
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
        ],
        [togglePanel],
    );

    const toolbarId = "top-bar-actions";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <ActionButtonRenderer toolbarId={toolbarId} />;
}
